import { useEffect, useState } from "react";
import {
  IconAlertTriangle,
  IconFileInvoice,
  IconLoader2,
  IconMail,
  IconPaperclip,
  IconSend,
  IconX,
} from "@tabler/icons-react";

interface InvoiceBulkActionConfirmModalProps {
  action: "generate_drafts" | "send_emails";
  scope?: "invoices" | "customers";
  selectedCount: number;
  totalSelectedCount?: number;
  customersSelectedCount?: number;
  invoicesWithoutDraftsCount?: number;
  onConfirm: (includeInvoicePdfs?: boolean) => void;
  onCancel: () => void;
  isOpen: boolean;
  isLoading?: boolean;
  isFetchingDetails?: boolean;
  emailAddresses?: string[];
  hasTemplates?: boolean;
}

export const InvoiceBulkActionConfirmModal = ({
  action,
  scope = "invoices",
  selectedCount,
  totalSelectedCount,
  customersSelectedCount,
  invoicesWithoutDraftsCount = 0,
  onConfirm,
  onCancel,
  isOpen,
  isLoading = false,
  isFetchingDetails = false,
  emailAddresses,
  hasTemplates = true,
}: InvoiceBulkActionConfirmModalProps) => {
  const [includeInvoicePdfs, setIncludeInvoicePdfs] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setIncludeInvoicePdfs(false);
    }
  }, [isOpen, action]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isLoading) {
        onCancel();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, isLoading, onCancel]);

  if (!isOpen) return null;

  const isGenerate = action === "generate_drafts";
  const isSend = action === "send_emails";
  const isWorking = isLoading;
  const canClose = !isWorking;

  const title = isGenerate ? "Generate email drafts" : "Send collection emails";
  const subtitle = isGenerate
    ? scope === "customers"
      ? "Drafts will be created for all open invoices of the selected customers. Existing drafts will be replaced."
      : "Drafts will be created from your configured email templates with customer and invoice details filled in. Existing drafts on selected invoices will be replaced."
    : scope === "customers"
      ? "Review recipients before sending drafts for selected customers from your connected mailbox."
      : "Review recipients before sending from your connected mailbox.";
  const confirmLabel = isGenerate ? "Generate drafts" : "Send emails";

  const handleConfirmClick = () => {
    if (isSend) {
      onConfirm(includeInvoicePdfs);
    } else {
      onConfirm();
    }
  };

  const showNoDraftsWarning =
    isSend &&
    !isFetchingDetails &&
    selectedCount === 0 &&
    (totalSelectedCount ?? 0) > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close modal"
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-[1px]"
        onClick={canClose ? onCancel : undefined}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-action-modal-title"
        className="relative z-10 flex w-full max-w-lg flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5">
          <div className="flex items-start gap-3">
            <div
              className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
                isGenerate
                  ? "bg-[#EEF4F2] text-[#4B7F73]"
                  : "bg-blue-50 text-blue-600"
              }`}
            >
              {isGenerate ? (
                <IconFileInvoice size={20} stroke={1.75} />
              ) : (
                <IconSend size={20} stroke={1.75} />
              )}
            </div>
            <div>
              <h2
                id="bulk-action-modal-title"
                className="text-lg font-semibold text-slate-900"
              >
                {title}
              </h2>
              <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={!canClose}
            aria-label="Close"
            className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <IconX size={18} />
          </button>
        </div>

        <div className="px-6 py-5">
          {isFetchingDetails ? (
            <div className="space-y-4">
              <div className="h-20 animate-pulse rounded-lg bg-slate-100" />
              <div className="h-24 animate-pulse rounded-lg bg-slate-100" />
            </div>
          ) : (
            <>
              <div className="rounded-lg border border-slate-200 bg-slate-50/80 px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      {isGenerate
                        ? scope === "customers"
                          ? "Customers selected"
                          : "Invoices selected"
                        : "Ready to send"}
                    </p>
                    <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">
                      {selectedCount}
                    </p>
                  </div>
                  {isSend &&
                  totalSelectedCount != null &&
                  totalSelectedCount > selectedCount ? (
                    <div className="text-right">
                      <p className="text-xs text-slate-500">
                        {scope === "customers" ? "Open invoices" : "Selected total"}
                      </p>
                      <p className="text-sm font-medium tabular-nums text-slate-700">
                        {totalSelectedCount}
                      </p>
                    </div>
                  ) : null}
                </div>
                <p className="mt-2 text-sm text-slate-600">
                  {isGenerate
                    ? scope === "customers"
                      ? `Draft emails will be created for all open invoices of ${selectedCount} customer${selectedCount === 1 ? "" : "s"}. You can review and edit them before sending.`
                      : `Draft emails will be created for ${selectedCount} invoice${selectedCount === 1 ? "" : "s"}. You can review and edit them before sending.`
                    : selectedCount > 0
                      ? scope === "customers" && customersSelectedCount != null
                        ? `${selectedCount} invoice${selectedCount === 1 ? "" : "s"} across ${customersSelectedCount} customer${customersSelectedCount === 1 ? "" : "s"} ${selectedCount === 1 ? "has" : "have"} a draft and ${selectedCount === 1 ? "is" : "are"} ready to send.`
                        : `${selectedCount} invoice${selectedCount === 1 ? " has" : "s have"} a draft and ${selectedCount === 1 ? "is" : "are"} ready to send.`
                      : scope === "customers"
                        ? "None of the open invoices for the selected customers have drafts yet."
                        : "None of the selected invoices have drafts yet."}
                </p>
              </div>

              {isGenerate && !hasTemplates ? (
                <div className="mt-4 flex gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                  <IconAlertTriangle
                    size={18}
                    className="mt-0.5 shrink-0 text-amber-600"
                  />
                  <div className="text-sm text-amber-900">
                    <p className="font-medium">No email templates</p>
                    <p className="mt-0.5 text-amber-800">
                      Configure at least one template in Settings → Email before
                      generating drafts.
                    </p>
                  </div>
                </div>
              ) : null}

              {showNoDraftsWarning ? (
                <div className="mt-4 flex gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                  <IconAlertTriangle
                    size={18}
                    className="mt-0.5 shrink-0 text-amber-600"
                  />
                  <div className="text-sm text-amber-900">
                    <p className="font-medium">No drafts available</p>
                    <p className="mt-0.5 text-amber-800">
                      Generate email drafts first, then return here to send.
                    </p>
                  </div>
                </div>
              ) : null}

              {isSend && invoicesWithoutDraftsCount > 0 && selectedCount > 0 ? (
                <div className="mt-4 flex gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
                  <IconAlertTriangle
                    size={18}
                    className="mt-0.5 shrink-0 text-slate-400"
                  />
                  <p className="text-sm text-slate-600">
                    {scope === "customers"
                      ? `${invoicesWithoutDraftsCount} open invoice${invoicesWithoutDraftsCount === 1 ? "" : "s"} across selected customers will be skipped because ${invoicesWithoutDraftsCount === 1 ? "it has" : "they have"} no draft yet.`
                      : `${invoicesWithoutDraftsCount} selected invoice${invoicesWithoutDraftsCount === 1 ? "" : "s"} will be skipped because ${invoicesWithoutDraftsCount === 1 ? "it has" : "they have"} no draft yet.`}
                  </p>
                </div>
              ) : null}

              {isSend && emailAddresses && emailAddresses.length > 0 ? (
                <div className="mt-4">
                  <div className="mb-2 flex items-center gap-2">
                    <IconMail size={16} className="text-slate-400" />
                    <p className="text-sm font-medium text-slate-700">
                      Recipients ({emailAddresses.length})
                    </p>
                  </div>
                  <div
                    className={`rounded-lg border border-slate-200 bg-white ${
                      emailAddresses.length > 5
                        ? "max-h-32 overflow-y-auto"
                        : ""
                    }`}
                  >
                    <ul className="divide-y divide-slate-100">
                      {emailAddresses.map((email) => (
                        <li
                          key={email}
                          className="px-3 py-2 text-sm text-slate-700"
                        >
                          {email}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : null}

              {isSend && selectedCount > 0 ? (
                <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 px-4 py-3 hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={includeInvoicePdfs}
                    onChange={(event) =>
                      setIncludeInvoicePdfs(event.currentTarget.checked)
                    }
                    disabled={isWorking}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#4B7F73] focus:ring-[#4B7F73]"
                  />
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5 text-sm font-medium text-slate-800">
                      <IconPaperclip size={15} className="text-slate-400" />
                      Attach invoice PDFs
                    </span>
                    <span className="mt-0.5 block text-sm text-slate-500">
                      Include a PDF copy of each invoice with the outgoing
                      email.
                    </span>
                  </span>
                </label>
              ) : null}
            </>
          )}
        </div>

        {isWorking ? (
          <div className="border-t border-slate-100 bg-slate-50 px-6 py-4">
            <div className="flex items-center gap-3">
              <IconLoader2 size={18} className="animate-spin text-[#4B7F73]" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-800">
                  {isGenerate ? "Generating drafts…" : "Sending emails…"}
                </p>
                <p className="text-xs text-slate-500">
                  This may take a moment for larger batches.
                </p>
              </div>
            </div>
            <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-200">
              <div className="h-full w-1/3 animate-pulse rounded-full bg-[#4B7F73]/70" />
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-3 border-t border-slate-100 px-6 py-4">
            <button
              type="button"
              onClick={onCancel}
              disabled={isFetchingDetails}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmClick}
              disabled={
                isFetchingDetails ||
                selectedCount === 0 ||
                (isGenerate && !hasTemplates) ||
                (isSend && showNoDraftsWarning)
              }
              className="inline-flex items-center gap-2 rounded-lg bg-[#4B7F73] px-4 py-2 text-sm font-medium text-white hover:bg-[#3F6C61] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isGenerate ? (
                <IconFileInvoice size={16} />
              ) : (
                <IconSend size={16} />
              )}
              {confirmLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
